import { isNaN, omitBy } from 'lodash';
import {
  remove,
  edit,
  create,
  updateDefaultControl,
  fetchAllIndeNodes
} from 'api/indenode';
import { REQUEST_SUCCESS, REQUEST_FAIL } from 'reducers/modal/actions';
import { getAllDevices } from 'reducers/device/actions';

const externallyManagedEdevs =
  window.env.REACT_APP_EXTERNALLY_MANAGED_EDEVS === 'true';

export const GET_ALL_INDENODES_SUCCESS = 'GET_ALL_INDENODES_SUCCESS';
export const DELETE_INDE_NODE_SUCCESS = 'DELETE_INDE_NODE_SUCCESS';
export const EDIT_INDE_NODE_SUCCESS = 'EDIT_INDE_NODE_SUCCESS';

export const SET_INDENODE_DATA = 'SET_INDENODE_DATA';

export const CREATE_INDE_NODE_SUCCESS = 'CREATE_INDE_NODE_SUCCESS';

export const UPDATE_DEFAULT_INDENODE_CONTROL_SUCCESS =
  'UPDATE_DEFAULT_INDENODE_CONTROL_SUCCESS';

export const SET_INDENODES_SUCCESS = 'SET_INDENODES_SUCCESS';

export const getAllIndeNodes = () => async (dispatch, getState) => {
  try {
    let indeNodes = await fetchAllIndeNodes();

    return dispatch({
      type: GET_ALL_INDENODES_SUCCESS,
      data: indeNodes
    });
  } catch (err) {
    // Redundant catch-throw, but maintaining this until we update polling
    throw err;
  }
};

export const duplicateIndeNode = indeNodeId => async (dispatch, getState) => {
  try {
    dispatch({
      type: REQUEST_FAIL,
      message: `Independent Node ${indeNodeId} already exists`
    });
  } catch (err) {
    return dispatch({
      type: REQUEST_FAIL,
      err
    });
  }
};

export const createIndeNode = indeNode => async (dispatch, getState) => {
  try {
    await create(indeNode);
    const allIndeNodes = getState().indeNode.indeNodes;
    let newIndeNodes = allIndeNodes.slice();
    if (newIndeNodes.length) {
      const newNode = { ...indeNode };
      if (externallyManagedEdevs) {
        newNode.localNode = true;
      }
      newIndeNodes.push(newNode);
    } else {
      newIndeNodes = await fetchAllIndeNodes();
    }
    dispatch({
      type: REQUEST_SUCCESS,
      message: `Independent Node ${indeNode.indeNodeId} create success`
    });
    dispatch({
      type: CREATE_INDE_NODE_SUCCESS,
      data: newIndeNodes
    });
    return { indeNodeId: indeNode.indeNodeId };
  } catch (err) {
    dispatch({
      type: REQUEST_FAIL,
      message: `Independent Node ${indeNode.indeNodeId} create failed`
    });
    throw err;
  }
};

export const deleteIndeNode = indeNodeId => async (dispatch, getState) => {
  try {
    await remove(indeNodeId);
    // NOTE: refetch indenodes and dispatch after delete, will be revisited during polling epic
    const updatedIndenodes = await dispatch(getAllIndeNodes());
    await dispatch(getAllDevices());
    dispatch({
      type: REQUEST_SUCCESS,
      message: `Indenode ${indeNodeId} delete success`
    });
    return updatedIndenodes;
  } catch (err) {
    dispatch({
      type: REQUEST_FAIL,
      message: `Indenode ${indeNodeId} delete failed`
    });
    throw err;
  }
};

export const editIndeNode =
  (indeNodeId, body) => async (dispatch, getState) => {
    try {
      await edit(indeNodeId, body);
      // NOTE: refetch indenodes and dispatch after edit, will be revisited during polling epic
      dispatch({
        type: REQUEST_SUCCESS,
        message: `Indenode ${indeNodeId} edit success`
      });
      return await dispatch(getAllIndeNodes());
    } catch (err) {
      dispatch({
        type: REQUEST_FAIL,
        message: `Indenode ${indeNodeId} edit failed`
      });
      throw err;
    }
  };

export const updateIndenodeDefaultControl =
  (nodeId, control) => async (dispatch, getState) => {
    try {
      const allIndeNodes = getState().indeNode.indeNodes;

      const filteredPayload = omitBy(
        control,
        value => value === null || value === '' || isNaN(value)
      );

      const reduxFilteredPayload = await updateDefaultControl(
        nodeId,
        filteredPayload
      );

      const updatedIndeNodes = allIndeNodes.map(obj => {
        if (obj.indeNodeId === nodeId) {
          obj.defaultControl = {
            ...reduxFilteredPayload
          };
        }
        return obj;
      });

      dispatch({
        type: REQUEST_SUCCESS,
        message: `Indenode ${nodeId} edit default control success`
      });
      return dispatch({
        type: UPDATE_DEFAULT_INDENODE_CONTROL_SUCCESS,
        data: updatedIndeNodes
      });
    } catch (err) {
      dispatch({
        type: REQUEST_FAIL,
        message: `Indenode ${nodeId} edit default control fail`
      });
      throw err;
    }
  };

export const setIndeNodeData =
  updatedIndeNode => async (dispatch, getState) => {
    const currentIndeNodes = getState().indeNode.indeNodes;
    const updatedIndeNodes = currentIndeNodes.map(indeNode =>
      indeNode.indeNodeId === updatedIndeNode.indeNodeId
        ? updatedIndeNode
        : indeNode
    );
    return dispatch({
      type: SET_INDENODE_DATA,
      data: updatedIndeNodes
    });
  };

export const setIndeNodes = controls => async (dispatch, getState) => {
  try {
    await dispatch({
      type: SET_INDENODES_SUCCESS,
      data: controls
    });
    return;
  } catch (err) {
    // Redundant catch-throw, but maintaining this until we update polling
    throw err;
  }
};

export const setIndeNodeDefaultControl =
  (indeNodeId, defaultControl) => async (dispatch, getState) => {
    try {
      const indeNodes = getState().indeNode.indeNodes;
      const updatedIndeNodes = indeNodes.map(indeNode => {
        if (indeNode.indeNodeId === indeNodeId) {
          const newIndeNode = { ...indeNode };
          newIndeNode.defaultControl = defaultControl;
          return newIndeNode;
        }
        return indeNode;
      });
      return dispatch({
        type: UPDATE_DEFAULT_INDENODE_CONTROL_SUCCESS,
        data: updatedIndeNodes
      });
    } catch (err) {
      // Redundant catch-throw, but maintaining this until we update polling
      throw err;
    }
  };
